import React from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import './BroadcastModal.css';

export interface Broadcast {
  id: number;
  title?: string;
  message: string;
  priority: 'high' | 'normal' | 'low';
  buttonText?: string;
  buttonUrl?: string;
}

interface BroadcastModalProps {
  broadcast: Broadcast;
  onClose: () => void;
  current?: number;
  total?: number;
}

/**
 * Преобразует URL в кликабельные ссылки в уже санитизированном HTML
 */
const linkifyUrls = (html: string): string => {
  // Регулярка для поиска текста вне тегов
  // Разбиваем HTML на части: теги и текст между ними
  const parts: string[] = [];
  const tagRegex = /(<[^>]+>)/g;
  let lastIndex = 0;
  let match;

  while ((match = tagRegex.exec(html)) !== null) {
    // Текст до тега - обрабатываем URL
    if (match.index > lastIndex) {
      const textBefore = html.substring(lastIndex, match.index);
      parts.push(processUrls(textBefore));
    }
    // Сам тег - оставляем как есть
    parts.push(match[0]);
    lastIndex = match.index + match[0].length;
  }

  // Оставшийся текст после последнего тега
  if (lastIndex < html.length) {
    parts.push(processUrls(html.substring(lastIndex)));
  }

  return parts.join('');
};

/**
 * Преобразует URL в ссылки в обычном тексте (без HTML тегов)
 */
const processUrls = (text: string): string => {
  const urlRegex = /(https?:\/\/[^\s<]+|t\.me\/[^\s<]+)/g;
  return text.replace(urlRegex, (url) => {
    const fullUrl = url.startsWith('http') ? url : `https://${url}`;
    return `<a href="${fullUrl}" target="_blank" rel="noopener noreferrer">${url}</a>`;
  });
};

/**
 * Блокирующее модальное окно для push-рассылок
 * Нет крестика закрытия - только кнопка "Понятно"
 */
const BroadcastModal: React.FC<BroadcastModalProps> = ({ broadcast, onClose, current, total }) => {
  const { t } = useTranslation();

  // Используем React's useMemo для обработки сообщения
  const sanitizedMessage = React.useMemo(() => {
    // СНАЧАЛА санитизируем - entities остаются как есть, реальные теги обрабатываются
    const sanitized = DOMPurify.sanitize(broadcast.message, {
      ALLOWED_TAGS: ['br', 'p'],
      ALLOWED_ATTR: [],
      KEEP_CONTENT: true
    });

    // Преобразуем URL в ссылки
    const messageWithLinks = linkifyUrls(sanitized);

    // Финальная санитизация с разрешением тегов <a>
    return DOMPurify.sanitize(messageWithLinks, {
      ALLOWED_TAGS: ['a', 'br', 'p'],
      ALLOWED_ATTR: ['href', 'target', 'rel'],
      KEEP_CONTENT: true
    });
  }, [broadcast.message]);

  const handleButtonClick = () => {
    if (broadcast.buttonUrl) {
      // Открываем ссылку в новой вкладке
      window.open(broadcast.buttonUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleOkClick = async () => {
    try {
      // Отмечаем как прочитанное
      await fetch(`/api/broadcasts/${broadcast.id}/read`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
    } catch (error) {
      console.error('Error marking broadcast as read:', error);
    }
    onClose();
  };

  // Рендерим через Portal напрямую в body для корректной работы backdrop-filter
  return ReactDOM.createPortal(
    <div className="broadcast-modal-overlay">
      <div className="broadcast-modal">
        {/* Заголовок */}
        {broadcast.title && (
          <div className="broadcast-modal-header">
            <h2 className="broadcast-modal-title">{broadcast.title}</h2>
          </div>
        )}

        {/* Контент */}
        <div className="broadcast-modal-content">
          {/* БЕЗОПАСНО: sanitizedMessage обработан DOMPurify с whitelist тегов [a,br,p] */}
          {/* eslint-disable-next-line react/no-danger */}
          <div
            className="broadcast-modal-message"
            dangerouslySetInnerHTML={{ __html: sanitizedMessage }}
          />
        </div>

        {/* Счетчик прогресса */}
        {current !== undefined && total !== undefined && total > 1 && (
          <div className="broadcast-modal-progress">
            <div className="progress-text">
              Уведомление {current} из {total}
            </div>
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${(current / total) * 100}%` }}
              />
            </div>
          </div>
        )}

        {/* Кнопки */}
        <div className="broadcast-modal-actions">
          {/* Дополнительная кнопка со ссылкой */}
          {broadcast.buttonText && broadcast.buttonUrl && (
            <button
              className="broadcast-modal-link-button"
              onClick={handleButtonClick}
            >
              {broadcast.buttonText}
            </button>
          )}

          {/* Основная кнопка закрытия - ОБЯЗАТЕЛЬНАЯ */}
          <button
            className="broadcast-modal-ok-button"
            onClick={handleOkClick}
          >
            {t('common.gotIt')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default BroadcastModal;
